import React, { useState } from 'react';
import { getIntl } from '../../../../utils/intl';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import PillSelect from '../../../v2BaseComponents/Inputs/PillSelect';
import { SplitFormRow } from '../../../v2BaseComponents/Form';
import ProviderFields from '../ProviderFields';
import { ProviderSupportList } from '../RenderWithProviders';
import useOrganization from '../../../../hooks/api/useOrganization';
import WithOrganizationUserField from './WithOrganizationUserField';
import LocationOrganizationGroupField from './LocationOrganizationGroupField';

const intl = getIntl('ProviderModeSection', {
    organization: 'Organization',
    provider: 'Provider',
    other: 'Other',
});

export type ProviderMode = 'organization' | 'provider' | 'other';

const MODE_OPTIONS: { labelKey: ProviderMode; value: ProviderMode }[] = [
    { labelKey: 'organization', value: 'organization' },
    { labelKey: 'provider', value: 'provider' },
    { labelKey: 'other', value: 'other' },
];

/**
 * Provider section shared by the family and pro visit forms: Organization (org "With" +
 * "Location", when the user has an organization) / Provider (care team select) / Other
 * (typed name + address). Family flows default to Provider; pro/org flows pass
 * defaultMode="organization".
 */
const ProviderModeSection: React.FC<{
    values: { resource: any };
    providers: ProviderSupportList;
    isDisabled: boolean;
    isLoading: boolean;
    setFieldValue: (field: string, value: any) => void;
    /** Starting mode for create flows. Org flows pass 'organization' (also forces the
     * Organization pill on while the org query is still loading). */
    defaultMode?: ProviderMode;
}> = ({ values, providers, isDisabled, isLoading, setFieldValue, defaultMode }) => {
    const { myOrganization } = useOrganization({});
    const hasOrganization = !!myOrganization?.organization?.organizationId || defaultMode === 'organization';
    const [mode, setMode] = useState<ProviderMode>(() => {
        if (values.resource.selectExistingProvider) return 'provider';
        // Any populated manual field means the visit was authored in Other mode — starting
        // in Provider mode would clear (and silently lose) those values on save.
        const hasManualProviderData = (
            ['providerName', 'address1', 'address2', 'city', 'state', 'zipCode'] as const
        ).some((field) => !!values.resource[field]);
        if (hasManualProviderData) return 'other';
        if (defaultMode === 'organization') return 'organization';
        if (defaultMode && (defaultMode !== 'provider' || providers.length)) return defaultMode;
        // No care team to pick from -> a Provider select would be permanently empty.
        return providers.length ? 'provider' : 'other';
    });
    const [hasPickedLocation, setHasPickedLocation] = useState(false);
    const hasAddressData = (['address1', 'address2', 'city', 'state', 'zipCode'] as const).some(
        (field) => !!values.resource[field],
    );

    // Default mode is Provider — make the form value agree on first render.
    React.useEffect(() => {
        if (mode === 'provider' && !values.resource.selectExistingProvider && providers.length) {
            setFieldValue('resource.selectExistingProvider', true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleModeChange = (nextMode: string | null) => {
        if (!nextMode || nextMode === mode) return;
        setMode(nextMode as ProviderMode);
        setHasPickedLocation(false);
        setFieldValue('resource.selectExistingProvider', nextMode === 'provider');
        // Explicitly clear the previous mode's fields — ProviderFields' own reset only
        // reacts to selectExistingProvider flips, which misses Organization <-> Other.
        setFieldValue('resource.providerCareTeamMemberId', null);
        setFieldValue('resource.providerName', null);
        (['address1', 'address2', 'city', 'state', 'zipCode'] as const).forEach((field) => {
            if (values.resource[field] !== undefined) {
                setFieldValue(`resource.${field}`, null);
            }
        });
    };

    return (
        <Stack vertical fullWidth space={contentStackSpacing}>
            <SplitFormRow>
                <PillSelect
                    options={MODE_OPTIONS.filter(
                        ({ value }) =>
                            // A Provider pill with no care team to pick from is a dead end
                            // (e.g. pro multi-client flows, which have no shared care team).
                            (value !== 'organization' || hasOrganization) &&
                            (value !== 'provider' || providers.length > 0),
                    ).map(({ labelKey, value }) => ({
                        label: intl(labelKey),
                        value,
                        testID: `visit-provider-mode-${value}`,
                    }))}
                    value={mode}
                    onChange={handleModeChange}
                    disableEmpty
                    isDisabled={isDisabled || isLoading}
                    testID="visit-provider-mode"
                />
            </SplitFormRow>
            {mode === 'organization' ? (
                <>
                    <WithOrganizationUserField isDisabled={isDisabled || isLoading} />
                    <LocationOrganizationGroupField
                        isDisabled={isDisabled || isLoading}
                        setFieldValue={setFieldValue}
                        onLocationSelected={() => setHasPickedLocation(true)}
                    />
                </>
            ) : null}
            <ProviderFields
                value={values}
                providers={providers}
                isDisabled={isDisabled}
                isLoading={isLoading}
                setFieldValue={setFieldValue}
                hideSelectToggle
                hideProviderNameInput={mode === 'organization'}
                // Org mode: the address block appears only once a Location fills it (or when the
                // visit already carries an address, e.g. edit flows). Other mode always shows it.
                hideAddressInputs={mode === 'organization' && !hasPickedLocation && !hasAddressData}
            />
        </Stack>
    );
};

export default React.memo(ProviderModeSection);
